// Programmes catalog — coaching + courses.
//
// Two audiences:
//   - business  → AI integration coaching for non-developer companies
//   - developer → 1:1 coaching + cohort courses with certification
//
// Format pulls from `services.js` conventions so the same Hero / Offer /
// Process / Deliverables card components work across the site.

// Stale-cohort gate (learn.md blocking rule; BLOCKERS A7). The next_cohort
// dates + seats_taken below are stale demo data — several dates are already
// past. Until Callum supplies real cohort dates + seat counts, NEVER render a
// date or a seats bar: pages show COHORT_TBD instead. Flip this to true only
// once every course record carries a real, future cohort date + seat count.
export const COHORTS_CONFIRMED = false;
export const COHORT_TBD = 'dates announced at enrolment — apply and we’ll confirm within one business day';

export const COACHING_TRACKS = [
  {
    slug: 'business',
    title: 'AI Coaching for Business',
    flag: 'New',
    audience: 'business',
    navCaption: 'AI workflows for non-technical teams',
    summary: `Hands-on coaching for founders, ops leaders, and entire teams who want to actually use AI day-to-day — not just hear about it. We meet you where your business is, audit your workflows, and embed the tools, prompts, and patterns that compress hours into minutes.`,
    accordion: `For founders, ops leaders, and entire teams. We audit your real workflows, choose the right tools for your stack, and embed <strong>AI patterns that compress hours into minutes</strong>. <strong>1:1 sessions £100/hour</strong>, or fixed-price 3 / 6 / 12-week programmes for teams that want a measurable transformation.`,
    hero: {
      eyebrow: 'AI coaching for business',
      title: 'AI, plugged into your business — not just talked about.',
      accent: 'plugged into your business',
      lead: `1:1 and team coaching for non-technical companies. We audit your real workflows, choose the right tools, and embed AI patterns that compress hours into minutes. £100/hour, or a fixed-price 3, 6, or 12-week programme.`,
      proofPoints: [
        { kicker: '£100/hr', value: '1:1 coaching' },
        { kicker: '3 / 6 / 12', value: 'week programmes' },
        { kicker: 'Measurable', value: 'hours saved' },
      ],
    },
    offer: {
      eyebrow: 'What you get',
      title: 'A working AI playbook for your business.',
      items: [
        { title: 'Workflow audit',          body: 'We map what your team does today, where the time goes, and which steps an AI tool can absorb. Out comes a prioritised hit-list.' },
        { title: 'Tool selection',          body: 'Which model, which app, which automation platform — chosen for your stack and budget, not for whoever paid for the ad you saw.' },
        { title: 'Prompt libraries',         body: 'Reusable, named prompts for your most common tasks. Stored in a place your team can edit, version, and share.' },
        { title: 'Live build sessions',      body: 'We sit with you on Zoom and wire it up. By the end of the call the automation is running on your data.' },
        { title: 'Team enablement',          body: 'Onboarding sessions for the people who will use it daily. Without buy-in from operators, AI dies on the vine.' },
        { title: 'Office hours',             body: 'Async + weekly live office hours for the duration of the programme. Bring a problem, leave with a working pattern.' },
      ],
    },
    process: {
      eyebrow: 'How it runs',
      title: 'Pick your pace. 1:1 or cohort. Always hands-on.',
      steps: [
        { num: '01', label: 'Sprint',  title: '1:1 coaching',           body: '£100/hour, billed in 60-minute blocks. Bring a specific workflow, leave with it automated. Booked through the platform calendar.' },
        { num: '02', label: '3 weeks', title: 'AI fast-start',          body: '£1,500. Six 90-minute sessions over three weeks. One workflow audited, three automations live, your team trained.' },
        { num: '03', label: '6 weeks', title: 'AI department',          body: '£3,500. Twelve sessions. Full workflow audit, prompt library, three to five automations in production, ops team trained.' },
        { num: '04', label: '12 weeks',title: 'AI transformation',      body: '£6,500. Whole-business audit, internal copilot, ten+ automations live, dashboards, on-call support for the duration.' },
      ],
    },
    deliverables: {
      eyebrow: 'You leave with',
      title: 'A working AI capability, not a slide deck.',
      items: [
        'Workflow audit document with prioritised opportunities',
        'A prompt library your team owns and edits',
        '3–10+ automations running on your real data',
        'Recordings of every session for your internal wiki',
        'A trained operator on your team who can keep building',
        '30 days of post-programme support and tuning',
      ],
    },
    cta: { primary: 'Book a 1:1 session', secondary: 'See programmes' },
  },

  {
    slug: 'developers',
    title: 'AI Coaching for Developers',
    flag: 'New',
    audience: 'developer',
    navCaption: '1:1 coaching to become an AI engineer',
    summary: `1:1 coaching with senior AI engineers. For developers who want to switch into the AI / automation engineer track — and for senior engineers who want to be twice as fast with Claude, Cursor, and the modern AI-native stack.`,
    accordion: `1:1 coaching with senior AI engineers for developers who want to make the switch into <strong>AI / automation engineering</strong>, and for seniors who want to be twice as fast with Claude, Cursor, MCP, and the AI-native stack. <strong>£100/hour</strong>, paired with structured 3, 6, and 12-week courses with <em>certification on completion</em>.`,
    hero: {
      eyebrow: 'AI coaching for developers',
      title: '1:1 with a senior, then a course that pays for itself.',
      accent: '1:1 with a senior',
      lead: `For developers who want to switch into the AI / automation engineer track, and for seniors who want to be twice as fast with Claude, Cursor, and the modern AI-native stack. £100/hour 1:1, or structured 3, 6, or 12-week courses with certification.`,
      proofPoints: [
        { kicker: '£100/hr', value: '1:1 coaching' },
        { kicker: 'Cert.',   value: 'on completion' },
        { kicker: 'Real',    value: 'paid projects' },
      ],
    },
    offer: {
      eyebrow: 'What you can pick',
      title: 'Two formats. Both shipped by senior engineers.',
      items: [
        { title: '1:1 coaching',          body: '£100/hour. Bring your repo or your roadmap, leave with momentum. Booked through the platform.' },
        { title: '3-week sprint',         body: '£395. The fast-start. Get fluent in Claude Code, MCP, evals, and the AI tooling that real teams use.' },
        { title: '6-week intensive',      body: '£795. Build a portfolio AI automation end-to-end, with code review and certification on pass.' },
        { title: '12-week career switch', body: '£1,495. Become a hireable AI / automation engineer. Two real projects, certification, intro to our bench.' },
        { title: 'Senior accelerator',    body: '£100/hour, no minimum. For seniors who want to be twice as fast with the modern AI-native stack.' },
        { title: 'Internal team training', body: 'From £2,500. Bring your engineering team. We train, evaluate, and certify against your codebase.' },
      ],
    },
    process: {
      eyebrow: 'How the courses run',
      title: 'Cohort-based, async + live, with a real project.',
      steps: [
        { num: '01', label: 'Apply',    title: 'Application + screening',  body: 'GitHub link, short note on what you want to build. Reviewed within one business day.' },
        { num: '02', label: 'Onboard',  title: 'Stripe, Slack, kit',       body: 'Stripe checkout, invite to the cohort Slack, repo access, tooling list. You start within a week.' },
        { num: '03', label: 'Build',    title: 'Live + async sessions',     body: 'Weekly 90-minute live sessions, async reviews, and a real project shipped against your name.' },
        { num: '04', label: 'Examine',  title: 'Capstone + certification',  body: 'Submit your capstone. We review, give feedback, and issue a verifiable certificate. Top performers get an interview for the bench.' },
      ],
    },
    deliverables: {
      eyebrow: 'You walk away with',
      title: 'A portfolio, a network, and an actual certificate.',
      items: [
        'A shipped portfolio automation in your GitHub',
        'A verifiable certificate (course code + issue date + public URL)',
        'Recordings of every session for re-watch',
        'A cohort Slack you can stay in after you graduate',
        'Direct intro to the out-of-house.dev bench for top performers',
        'Lifetime access to course updates',
      ],
    },
    cta: { primary: 'Book a 1:1 session', secondary: 'See courses' },
  },
];

export const COURSES = [
  {
    slug: 'ai-fast-start-3w',
    track: 'developers',
    duration_weeks: 3,
    duration_label: '3-week sprint',
    name: 'AI Fast-Start (3 weeks)',
    tagline: 'Get fluent in Claude, MCP, and the AI-native stack.',
    price_gbp: 395,
    price_label: '£395',
    next_cohort: '2026-06-15',
    seats: 20,
    seats_taken: 14,
    certificate: true,
    cert_difficulty: 'Foundation',
    outcomes: [
      'Confidently use Claude Code, Cursor, and the MCP ecosystem',
      'Write effective prompts and own a personal prompt library',
      'Build small automations that hit external APIs',
      'Understand RAG, embeddings, and vector stores at a working level',
      'Read AI logs and traces, debug a stuck agent',
    ],
    modules: [
      { week: 1, title: 'Foundations', items: ['Claude API + Anthropic SDK', 'Prompt engineering as production code', 'Tooling: Claude Code, Cursor, MCP', 'Capstone brief picked'] },
      { week: 2, title: 'Building', items: ['First automation wired end-to-end', 'Working with structured outputs (JSON, schemas)', 'Tool use + function calling', 'Capstone scoping reviewed'] },
      { week: 3, title: 'Shipping', items: ['Evals: how to measure your agent', 'Traces + observability basics', 'Capstone submission + review', 'Certification exam (multiple choice + code)'] },
    ],
    cta: { primary: 'Enrol now', secondary: 'See syllabus' },
  },
  {
    slug: 'ai-builder-6w',
    track: 'developers',
    duration_weeks: 6,
    duration_label: '6-week intensive',
    name: 'AI Builder Intensive (6 weeks)',
    tagline: 'Build and ship a portfolio AI automation with senior code review.',
    price_gbp: 795,
    price_label: '£795',
    next_cohort: '2026-07-06',
    seats: 16,
    seats_taken: 6,
    certificate: true,
    cert_difficulty: 'Intermediate',
    outcomes: [
      'Ship a portfolio AI automation graded by senior engineers',
      'Confidently use Claude, GPT, and small/cheap models in tandem',
      'Build multi-step agents with safe tool use and recovery',
      'Wire RAG, vector search, and hybrid retrieval against real corpora',
      'Set up evals that catch regressions before they hit production',
      'Earn the Intermediate certificate (publicly verifiable)',
    ],
    modules: [
      { week: 1, title: 'Foundations',           items: ['AI-native engineering mindset', 'Stack survey', 'Project brief locked'] },
      { week: 2, title: 'Architecture',          items: ['Picking a model per call', 'Tool use, function calling, structured outputs', 'Architecture diagram for capstone'] },
      { week: 3, title: 'Retrieval',             items: ['Embeddings + vector stores', 'Hybrid retrieval', 'Citations + groundedness'] },
      { week: 4, title: 'Agents',                items: ['Multi-step agents', 'Planning + executing + reviewing roles', 'Failure modes + recovery'] },
      { week: 5, title: 'Quality',               items: ['Eval suites: what to score, how', 'Traces + observability', 'Cost + token optimisation'] },
      { week: 6, title: 'Ship + certify',        items: ['Capstone hardening', 'Code review with a senior eng', 'Certification exam'] },
    ],
    cta: { primary: 'Enrol now', secondary: 'See syllabus' },
  },
  {
    slug: 'ai-engineer-12w',
    track: 'developers',
    duration_weeks: 12,
    duration_label: '12-week career switch',
    name: 'AI / Automation Engineer (12 weeks)',
    tagline: 'Become a hireable AI engineer. Two real projects, certification, bench intro.',
    flag: 'Flagship',
    price_gbp: 1495,
    price_label: '£1,495',
    next_cohort: '2026-06-22',
    seats: 12,
    seats_taken: 3,
    certificate: true,
    cert_difficulty: 'Professional',
    outcomes: [
      'Be employable as an AI / automation engineer',
      'Ship two end-to-end automation projects to portfolio standard',
      'Pass a professional-grade exam covering eval, observability, safety',
      'Receive a senior interview for the out-of-house.dev bench on completion',
      'Earn the Professional certificate (publicly verifiable)',
    ],
    modules: [
      { week: 1,  title: 'Week 01 — Orientation',      items: ['Career path map', 'Toolchain installed', 'First commit'] },
      { week: 2,  title: 'Week 02 — Prompting',         items: ['Prompts as code', 'Prompt evals', 'Library + versioning'] },
      { week: 3,  title: 'Week 03 — Tool use',           items: ['Function calling', 'Structured outputs', 'JSON-mode safely'] },
      { week: 4,  title: 'Week 04 — RAG',                items: ['Embeddings, indexing, retrieval', 'Hybrid search', 'Groundedness'] },
      { week: 5,  title: 'Week 05 — Project A start',    items: ['Brief: inbox-triage agent', 'Architecture review', 'Build sprint'] },
      { week: 6,  title: 'Week 06 — Agents',             items: ['Multi-step + multi-agent', 'Recovery + failure modes', 'Project A demo'] },
      { week: 7,  title: 'Week 07 — Evals',              items: ['Eval suites', 'Regression catching', 'Quality dashboards'] },
      { week: 8,  title: 'Week 08 — Observability',      items: ['Traces, logs, replays', 'Debugging agents like code', 'Project B brief picked'] },
      { week: 9,  title: 'Week 09 — Project B start',    items: ['Brief: internal copilot', 'RAG over docs', 'Build sprint'] },
      { week: 10, title: 'Week 10 — Operationalising',   items: ['Token cost + caching', 'Multi-model routing', 'Deploy + CI/CD'] },
      { week: 11, title: 'Week 11 — Safety + ethics',    items: ['Refusals + abuse', 'Privacy + GDPR', 'Responsible disclosure'] },
      { week: 12, title: 'Week 12 — Capstone + exam',    items: ['Project B demo', 'Certification exam', 'Bench interview'] },
    ],
    cta: { primary: 'Apply for next cohort', secondary: 'See syllabus' },
  },
  {
    slug: 'business-ai-fast-3w',
    track: 'business',
    duration_weeks: 3,
    duration_label: '3-week fast-start',
    name: 'Business AI Fast-Start (3 weeks)',
    tagline: 'One workflow audited, three automations live, your team trained.',
    price_gbp: 1500,
    price_label: '£1,500',
    next_cohort: '2026-06-08',
    seats: 8,
    seats_taken: 4,
    certificate: false,
    outcomes: [
      'A documented workflow audit you can re-use as a model',
      'Three production automations running on your real data',
      'A prompt library shared across the team',
      'Operators trained to keep extending the work',
    ],
    modules: [
      { week: 1, title: 'Audit',  items: ['Workflow mapping', 'Tool selection', 'Quick-win identified'] },
      { week: 2, title: 'Build',  items: ['Automation 1 live', 'Automation 2 live', 'Prompt library set up'] },
      { week: 3, title: 'Train',  items: ['Automation 3 live', 'Operator training', 'Hand-off doc'] },
    ],
    cta: { primary: 'Book a fast-start', secondary: 'See programmes' },
  },
  {
    slug: 'business-ai-department-6w',
    track: 'business',
    duration_weeks: 6,
    duration_label: '6-week department',
    name: 'Build an AI Department (6 weeks)',
    tagline: 'Full workflow audit, prompt library, 3–5 automations in production.',
    price_gbp: 3500,
    price_label: '£3,500',
    next_cohort: '2026-07-13',
    seats: 6,
    seats_taken: 2,
    certificate: false,
    outcomes: [
      'A full operating manual for AI in your business',
      'A prompt library you own and version',
      '3 to 5 live automations in production',
      'A trained operator on your team who can keep building',
    ],
    modules: [
      { week: 1, title: 'Audit',  items: ['Whole-business workflow map', 'Stakeholder interviews', 'Roadmap signed off'] },
      { week: 2, title: 'Setup', items: ['Tooling agreed', 'Slack + Notion ops surface', 'Prompt library skeleton'] },
      { week: 3, title: 'Build I', items: ['Automation 1 + 2 live', 'Eval suites set up'] },
      { week: 4, title: 'Build II', items: ['Automation 3 + 4 live', 'Dashboards stood up'] },
      { week: 5, title: 'Operator training', items: ['Operator workshops', 'Run-book written', 'Hand-off rehearsal'] },
      { week: 6, title: 'Hand-off + review', items: ['Automation 5 (if scoped)', 'Hand-off + final review', '30-day follow-up booked'] },
    ],
    cta: { primary: 'Scope a department build', secondary: 'See programmes' },
  },
  {
    slug: 'business-ai-transformation-12w',
    track: 'business',
    duration_weeks: 12,
    duration_label: '12-week transformation',
    name: 'AI Business Transformation (12 weeks)',
    tagline: 'Internal copilot, 10+ automations, dashboards, on-call support.',
    flag: 'Flagship',
    price_gbp: 6500,
    price_label: '£6,500',
    next_cohort: '2026-08-03',
    seats: 4,
    seats_taken: 1,
    certificate: false,
    outcomes: [
      'A full internal copilot grounded in your docs and data',
      '10 or more production automations, all monitored',
      'Live dashboards for your leadership team',
      'On-call support for the duration of the programme',
      'A capable in-house operator to keep building post-programme',
    ],
    modules: [
      { week: 1,  title: 'Week 01 — Discovery',     items: ['Whole-business audit', 'Stakeholder interviews', 'Strategy doc drafted'] },
      { week: 2,  title: 'Week 02 — Strategy lock', items: ['Roadmap signed off', 'Tooling decided', 'Surface area scoped'] },
      { week: 3,  title: 'Week 03 — Quick wins',    items: ['Automations 1–2 live', 'Operator onboarded'] },
      { week: 4,  title: 'Week 04 — Department lift', items: ['Automations 3–4 live', 'Department 1 covered'] },
      { week: 5,  title: 'Week 05 — Knowledge base', items: ['Internal copilot indexed', 'RAG up'] },
      { week: 6,  title: 'Week 06 — Copilot live',    items: ['Copilot live in Slack', 'Team training'] },
      { week: 7,  title: 'Week 07 — Department lift II', items: ['Automations 5–6 live', 'Department 2 covered'] },
      { week: 8,  title: 'Week 08 — Sales + GTM',     items: ['Outreach automation', 'AI content engine'] },
      { week: 9,  title: 'Week 09 — Ops + finance',   items: ['Invoice + reconciliation automations', 'Reporting copilots'] },
      { week: 10, title: 'Week 10 — Dashboards',      items: ['Live dashboards', 'Eval drift watchers'] },
      { week: 11, title: 'Week 11 — Hand-off',        items: ['Operator certification', 'Run-book locked'] },
      { week: 12, title: 'Week 12 — Review',          items: ['Outcome measurement', 'Phase II roadmap'] },
    ],
    cta: { primary: 'Book a transformation call', secondary: 'See programmes' },
  },
];

export const getCourseBySlug = (slug) => COURSES.find((c) => c.slug === slug);
export const getCoachingTrack = (slug) => COACHING_TRACKS.find((t) => t.slug === slug);

export const COACHING_PRICING_HOURLY_GBP = 100;
